import React from 'react';
import { formatTime } from '../../../utils/job-utils';
import { JOB_STATUSES } from '../constants/editJobConstants';
import AssignmentSection from './AssignmentSection';
import ServiceTypeSection from './ServiceTypeSection';
import FinancialSection from './FinancialSection';
import FlipOverrideSection from './FlipOverrideSection';
import CustomFlagManager from '../../custom-flags/CustomFlagManager';
import JobEditHistory from './JobEditHistory';
const JobEditForm = ({ 
  job,
  formData,
  setFormData,
  handleChange,
  assignmentType,
  setAssignmentType,
  selectedTeamId,
  setSelectedTeamId,
  sortedActiveUsers,
  teamsData,
  assignmentDataLoading,
  usersLoading,
  teamsLoading,
  availableServiceTypes,
  canSeeFinancialFields,
  user,
  jobCheckoutTime,
  displayTime,
  previousReservation,
  // Cancel job props
  handleCancelJob,
  canCancelJob,
  cancelJobLoading,
  // Custom flag props
  onCustomFlagChange,
  historyRefreshKey
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded p-3 text-sm text-blue-700 flex items-start">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>Changes made here will only affect this individual job. Use the <strong>Edit Reservation</strong> tab to update all jobs associated with this reservation.</span>
      </div>
      
      {/* Add info about previous reservation tab for CHECKIN-based jobs */}
      {previousReservation && job?.serviceType?.timeBase === 'CHECKIN' && (
        <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-700 flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span>
            This {job.serviceType?.title} job starts when the previous guest checks out. Use the Edit Reservation tab to navigate to the previous reservation to change checkout times.
          </span>
        </div>
      )}

      <AssignmentSection 
        assignmentType={assignmentType}
        setAssignmentType={setAssignmentType}
        formData={formData}
        handleChange={handleChange}
        sortedActiveUsers={sortedActiveUsers}
        selectedTeamId={selectedTeamId}
        setSelectedTeamId={setSelectedTeamId}
        teamsData={teamsData}
        assignmentDataLoading={assignmentDataLoading}
        usersLoading={usersLoading}
        teamsLoading={teamsLoading}
        scheduledDate={formData?.scheduledDate || job?.scheduledDate || null}
      />

      <ServiceTypeSection 
        formData={formData}
        handleChange={handleChange}
        availableServiceTypes={availableServiceTypes}
        job={job}
      />

      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-600 p-2 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm rounded-md"
            >
              {JOB_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          {(!job.isDeleted && canCancelJob) && (
            <button
              type="button"
              onClick={handleCancelJob}
              disabled={cancelJobLoading}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
            >
              {cancelJobLoading ? 'Canceling...' : 'Cancel Job'}
            </button>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-700">
          Scheduled Date
        </label>
        <input
          type="date"
          name="scheduledDate"
          id="scheduledDate"
          value={formData.scheduledDate}
          onChange={handleChange}
          disabled={!formData.hasDateOverride}
          className={`mt-1 block w-full border border-gray-600 p-2 rounded-md shadow-sm ${
            !formData.hasDateOverride ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''
          } focus:ring-sky-500 focus:border-sky-500 sm:text-sm`}
        />
        <div className="mt-1 flex items-center">
          <input
            type="checkbox"
            id="hasDateOverride"
            name="hasDateOverride"
            checked={formData.hasDateOverride}
            onChange={handleChange}
            className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
          />
          <label htmlFor="hasDateOverride" className="ml-2 block text-sm text-gray-700">
            Override date for this job only (prevent updates from reservation changes)
          </label>
        </div>
      </div>

      <div>
        <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
          {formData.hasTimeOverride 
            ? <span>
                Start Time <span className="text-sky-600">(Override)</span>
                {jobCheckoutTime && <span className="ml-2 line-through text-gray-500">{formatTime(jobCheckoutTime)}</span>}
              </span>
            : `Start Time: ${formatTime(displayTime)}`}
        </label>
        <input
          type="time"
          name="startTime"
          id="startTime"
          value={formData.startTime || ''}
          onChange={handleChange}
          disabled={!formData.hasTimeOverride}
          className={`mt-1 block w-full border border-gray-600 p-2 rounded-md shadow-sm ${
            !formData.hasTimeOverride ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''
          } focus:ring-sky-500 focus:border-sky-500 sm:text-sm`}
        />
        {formData.hasTimeOverride && formData.startTime && (
          <p className="mt-1 text-sm text-sky-600 font-medium">
            Updated Start Time: {formatTime(formData.startTime)}
          </p>
        )}
        <div className="mt-1 flex items-center">
          <input
            type="checkbox"
            id="hasTimeOverride"
            name="hasTimeOverride"
            checked={formData.hasTimeOverride}
            onChange={handleChange}
            className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
          />
          <label htmlFor="hasTimeOverride" className="ml-2 block text-sm text-gray-700">
            Override time for this job only (prevent updates from reservation changes)
          </label>
        </div>
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          value={formData.notes}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-600 p-2 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        />
      </div>

      {/* Custom Flags Section */}
      <CustomFlagManager 
        job={job}
        onFlagChange={onCustomFlagChange}
      />

      <JobEditHistory jobId={job?.id} refreshKey={historyRefreshKey} />

      <FlipOverrideSection 
        job={job}
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        user={user}
      />

      <FinancialSection 
        job={job}
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        canSeeFinancialFields={canSeeFinancialFields}
      />
    </div>
  );
};

export default JobEditForm;
